import type { PortalRole } from '../../app/navigation';
import type { WorkCategory } from '../worklogs/worklog-model';

export type BillingMode = 'FIXED' | 'HOURLY' | 'PACKAGE';
export type BillingWorkspaceTab = 'periods' | 'tariffs' | 'categories';

export type BillingConfigurationIssue = {
  code: string;
  categoryCode?: string;
  categoryName?: string;
  message: string;
};

export type BillingCategoryLine = {
  workCategoryId: string;
  categoryCode: string;
  categoryName: string;
  hourlyRate: string;
  roundingMinutes: number;
  minimumMinutes: number;
  configuredIncludedMinutes?: number;
  actualMinutes: number;
  billableMinutes: number;
  roundedMinutes: number;
  includedMinutes: number;
  chargeableMinutes: number;
  incomingRolloverMinutes: number;
  outgoingRolloverMinutes: number;
  amount: string;
};

export type BillingWorklogLine = {
  worklogId: string;
  ticketId: string;
  ticketNumber: number;
  ticketSubject: string;
  ticketClosedAt: string;
  userId: string;
  workCategoryId: string;
  categoryCode: string;
  categoryName: string;
  workedAt: string;
  isBillable: boolean;
  actualMinutes: number;
  billableMinutes: number;
  roundedMinutes: number;
  includedMinutes: number;
  chargeableMinutes: number;
  hourlyRate: string;
  amount: string;
};

export type BillingTicketLine = {
  ticketId: string;
  ticketNumber: number;
  ticketSubject: string;
  ticketClosedAt: string;
  worklogCount: number;
  actualMinutes: number;
  billableMinutes: number;
  roundedMinutes: number;
  includedMinutes: number;
  chargeableMinutes: number;
  amount: string;
};

export type BillingTotals = {
  actualMinutes: number;
  billableMinutes: number;
  roundedMinutes: number;
  includedMinutes: number;
  chargeableMinutes: number;
  baseAmount: string;
  variableAmount: string;
  totalAmount: string;
};

export type TariffCategoryRate = {
  workCategoryId: string;
  categoryCode: string;
  categoryName: string;
  hourlyRate: string;
  includedMinutes: number;
  roundingMinutes: number;
  minimumMinutes: number;
  rolloverEnabled: boolean;
  rolloverCapMinutes: number;
};

export type TariffVersion = {
  id: string;
  companyId: string;
  version: number;
  name: string;
  mode: BillingMode;
  validFrom: string;
  baseAmount: string;
  calculationVersion?: string;
  categoryRates: TariffCategoryRate[];
};

export type BillingPeriod = {
  status: 'LIVE';
  company: { id: string; name: string };
  periodMonth: string;
  closedTicketCount: number;
  tariffVersion: Omit<TariffVersion, 'companyId' | 'categoryRates'> & { version: number | null };
  calculationVersion: string;
  totals: BillingTotals;
  categoryLines: BillingCategoryLine[];
  worklogLines: BillingWorklogLine[];
  ticketLines: BillingTicketLine[];
};

export type BillingSummaryRow = {
  company_id: string;
  company_name: string;
  period: string;
  period_status: 'LIVE';
  calculation_status: 'LIVE' | 'CONFIGURATION_ERROR';
  tariff_version_id?: string | null;
  tariff_version?: number | null;
  tariff_name?: string | null;
  tariff_mode?: BillingMode | null;
  total_minutes: number;
  billable_minutes: number;
  rounded_minutes: number;
  included_minutes: number;
  chargeable_minutes: number;
  amount: string | number;
  closed_ticket_count: number;
  category_lines: BillingCategoryLine[];
  ticket_lines: BillingTicketLine[];
  configuration_issues: BillingConfigurationIssue[];
  configuration_message?: string;
};

export type BillingCompany = {
  company_id: string;
  company_name: string;
  tariff_version_id?: string | null;
  tariff_version?: number | null;
  tariff_name?: string | null;
  tariff_mode?: BillingMode | null;
  valid_from?: string | null;
  base_amount?: string | number;
  hourly_rate?: string | number;
  rounding_minutes?: number;
  minimum_minutes?: number;
  included_minutes?: number;
  is_active?: boolean;
};

export type TariffPresetRate = {
  categoryCode: string;
  includedMinutes: number;
  hourlyRate?: string | number | null;
  roundingMinutes: number;
  minimumMinutes: number;
  rolloverEnabled: boolean;
  rolloverCapMinutes: number;
};

export type TariffPreset = {
  id: string;
  code: string;
  name: string;
  mode: BillingMode;
  baseAmount: string;
  categoryRates: TariffPresetRate[];
};

export type PersonalWorklog = {
  id: string;
  ticket_id: string;
  ticket_number: number;
  ticket_subject: string;
  duration_minutes: number;
  is_billable: boolean;
  worked_at: string;
  billing_status: 'OPEN' | 'APPROVED' | 'CLOSED';
  work_category_id?: string | null;
  category_code?: string | null;
  category_name?: string | null;
};

export type PersonalWorklogSummary = {
  period: string;
  rows: PersonalWorklog[];
  summary: {
    total_minutes: number;
    billable_minutes: number;
    by_category: Array<{
      work_category_id?: string | null;
      category_code?: string | null;
      category_name?: string | null;
      total_minutes: number;
      billable_minutes: number;
    }>;
  };
};

export type TariffRateDraft = {
  enabled: boolean;
  hourlyRate: string;
  includedMinutes: string;
  roundingMinutes: string;
  minimumMinutes: string;
  rolloverEnabled: boolean;
  rolloverCapMinutes: string;
};

export type TariffDraft = {
  name: string;
  mode: BillingMode;
  validFromMonth: string;
  baseAmount: string;
  rates: Record<string, TariffRateDraft>;
};

export const FINANCIAL_ROLES = new Set<PortalRole>(['ADMIN', 'MANAGER']);

export function billingTabs(role: PortalRole): BillingWorkspaceTab[] {
  if (role === 'ADMIN' || role === 'MANAGER') return ['periods', 'tariffs', 'categories'];
  return [];
}

export function isPersonalBillingRole(role: PortalRole) {
  return role === 'DISPATCHER' || role === 'EXECUTOR';
}

export function currentMoscowMonth() {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
  }).format(new Date());
}

export function modeLabel(mode?: BillingMode | null) {
  return ({ FIXED: 'Фиксированный', HOURLY: 'Почасовой', PACKAGE: 'Пакетный' } as Record<string, string>)[String(mode || '')] || 'Не настроен';
}

export function emptyRateDraft(): TariffRateDraft {
  return {
    enabled: false,
    hourlyRate: '',
    includedMinutes: '0',
    roundingMinutes: '30',
    minimumMinutes: '30',
    rolloverEnabled: false,
    rolloverCapMinutes: '0',
  };
}

export function emptyTariffDraft(categories: WorkCategory[]): TariffDraft {
  return {
    name: '',
    mode: 'HOURLY',
    validFromMonth: currentMoscowMonth(),
    baseAmount: '',
    rates: Object.fromEntries(categories.filter(category => category.is_active).map(category => [category.id, emptyRateDraft()])),
  };
}

function monthAfter(value: string) {
  const match = /^(\d{4})-(\d{2})/.exec(value);
  if (!match) return currentMoscowMonth();
  const year = Number(match[1]);
  const month = Number(match[2]);
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
}

export function tariffDraftFromVersion(version: TariffVersion, categories: WorkCategory[]): TariffDraft {
  const rates = Object.fromEntries(categories.filter(category => category.is_active).map(category => {
    const source = version.categoryRates.find(rate => rate.workCategoryId === category.id);
    return [category.id, source ? {
      enabled: true,
      hourlyRate: String(source.hourlyRate),
      includedMinutes: String(source.includedMinutes ?? 0),
      roundingMinutes: String(source.roundingMinutes ?? 30),
      minimumMinutes: String(source.minimumMinutes ?? 30),
      rolloverEnabled: Boolean(source.rolloverEnabled),
      rolloverCapMinutes: String(source.rolloverCapMinutes ?? 0),
    } : emptyRateDraft()];
  }));
  return {
    name: version.name,
    mode: version.mode,
    validFromMonth: monthAfter(version.validFrom),
    baseAmount: String(version.baseAmount),
    rates,
  };
}

export function applyTariffPreset(draft: TariffDraft, preset: TariffPreset, categories: WorkCategory[]): TariffDraft {
  const rates = Object.fromEntries(categories.filter(category => category.is_active).map(category => {
    const source = preset.categoryRates.find(rate => rate.categoryCode === category.code);
    return [category.id, source ? {
      enabled: true,
      hourlyRate: source.hourlyRate === null || source.hourlyRate === undefined ? '' : String(source.hourlyRate),
      includedMinutes: String(source.includedMinutes ?? 0),
      roundingMinutes: String(source.roundingMinutes ?? 30),
      minimumMinutes: String(source.minimumMinutes ?? 30),
      rolloverEnabled: Boolean(source.rolloverEnabled),
      rolloverCapMinutes: String(source.rolloverCapMinutes ?? 0),
    } : emptyRateDraft()];
  }));
  return {
    ...draft,
    name: preset.name,
    mode: preset.mode,
    baseAmount: String(preset.baseAmount),
    rates,
  };
}

export function tariffDraftError(draft: TariffDraft, categories: WorkCategory[]) {
  if (!draft.name.trim()) return 'Введите название тарифа';
  if (!/^\d{4}-(?:0[1-9]|1[0-2])$/.test(draft.validFromMonth)) return 'Укажите месяц начала действия';
  if ((draft.mode === 'FIXED' || draft.mode === 'PACKAGE') && !(Number(draft.baseAmount) > 0)) return 'Укажите базовую сумму';
  if (draft.mode === 'FIXED') return '';
  const enabled = categories.filter(category => category.is_active && draft.rates[category.id]?.enabled);
  if (!enabled.length) return 'Выберите хотя бы одну категорию работ';
  for (const category of enabled) {
    const rate = draft.rates[category.id];
    if (!(Number(rate.hourlyRate) >= 0) || rate.hourlyRate.trim() === '') {
      return draft.mode === 'PACKAGE'
        ? `Укажите ставку превышения для категории «${category.name}»`
        : `Укажите ставку для категории «${category.name}»`;
    }
    if (draft.mode === 'PACKAGE' && !(Number(rate.includedMinutes) > 0)) return `Укажите включённые минуты для категории «${category.name}»`;
    if (!(Number(rate.roundingMinutes) >= 1)) return `Укажите шаг округления для категории «${category.name}»`;
    if (!(Number(rate.minimumMinutes) >= 0)) return `Укажите минимальное время для категории «${category.name}»`;
  }
  return '';
}

export function tariffPayload(draft: TariffDraft, categories: WorkCategory[]) {
  const error = tariffDraftError(draft, categories);
  if (error) throw new Error(error);
  return {
    name: draft.name.trim(),
    mode: draft.mode,
    validFrom: `${draft.validFromMonth}-01`,
    baseAmount: draft.mode === 'HOURLY' ? '0.00' : draft.baseAmount,
    categoryRates: draft.mode === 'FIXED' ? [] : categories
      .filter(category => category.is_active && draft.rates[category.id]?.enabled)
      .map(category => {
        const rate = draft.rates[category.id];
        return {
          workCategoryId: category.id,
          hourlyRate: rate.hourlyRate,
          includedMinutes: draft.mode === 'PACKAGE' ? Number(rate.includedMinutes) : 0,
          roundingMinutes: Number(rate.roundingMinutes),
          minimumMinutes: Number(rate.minimumMinutes),
          rolloverEnabled: draft.mode === 'PACKAGE' && rate.rolloverEnabled,
          rolloverCapMinutes: draft.mode === 'PACKAGE' && rate.rolloverEnabled ? Number(rate.rolloverCapMinutes) : 0,
        };
      }),
  };
}
